// Static reference content for the Specimen Collection & Transport section.

// Tube reference: keys map to lucide-react tints used in the panel.
export const TUBES = [
  {
    id: "lavender", color: "Lavender (EDTA)", hex: "#7E22CE", bg: "rgba(126,34,206,0.10)",
    additive: "EDTA (anticoagulant — chelates calcium)",
    uses: ["CBC", "Hemoglobin A1C", "Hematology Testing"],
    processing: "No clotting required. Invert 8–10× gently. Transport whole blood.",
    handling: "Room temperature. Do not centrifuge unless plasma is required.",
  },
  {
    id: "gold", color: "Gold / SST", hex: "#B45309", bg: "rgba(180,83,9,0.10)",
    additive: "Clot activator + gel separator",
    uses: ["CMP", "BMP", "Lipid Panel", "Chemistry Testing"],
    processing: "Allow to clot 30 min upright, then centrifuge at 1300×g for 10 min.",
    handling: "Refrigerate serum aliquots if not transported within 4 hours.",
  },
  {
    id: "red", color: "Red", hex: "#B91C1C", bg: "rgba(185,28,28,0.10)",
    additive: "Plain (no additive) or clot activator",
    uses: ["Serum Testing", "Special Chemistry"],
    processing: "Allow to clot 30–60 min, centrifuge, aliquot serum.",
    handling: "Refrigerate aliquots; freeze for long-term hold per test.",
  },
  {
    id: "light_blue", color: "Light Blue", hex: "#2563EB", bg: "rgba(37,99,235,0.10)",
    additive: "Sodium citrate (3.2%)",
    uses: ["PT / INR", "Coagulation Studies"],
    processing: "Critical: fill to the line. Invert 3–4×. Centrifuge 1500×g for 15 min.",
    handling: "Run within 4 hours or freeze plasma. Discrepant fills = REJECT.",
  },
  {
    id: "green", color: "Green", hex: "#15803D", bg: "rgba(21,128,61,0.10)",
    additive: "Sodium or lithium heparin",
    uses: ["STAT Chemistry", "Plasma Testing"],
    processing: "Invert 8–10×. Centrifuge promptly; plasma is ready immediately.",
    handling: "Refrigerate plasma. Heparin interferes with some immunoassays — confirm.",
  },
  {
    id: "gray", color: "Gray", hex: "#475569", bg: "rgba(71,85,105,0.10)",
    additive: "Sodium fluoride + potassium oxalate (glycolysis inhibitor)",
    uses: ["Glucose Testing", "Lactate Testing"],
    processing: "Invert 8–10×. Use immediately for accurate glucose/lactate.",
    handling: "Refrigerate. Avoid hemolysis.",
  },
];

// Order of Draw — per CLSI standards.
export const ORDER_OF_DRAW = [
  { step: 1, name: "Blood Culture Tubes", color: "#FACC15", hex: "#A16207", note: "Sterility first — always before anything else." },
  { step: 2, name: "Light Blue", color: "#2563EB", hex: "#1D4ED8", note: "Coag tubes early — additive carryover matters most here." },
  { step: 3, name: "Red", color: "#B91C1C", hex: "#7F1D1D", note: "Serum tubes (plain) before any additive tubes." },
  { step: 4, name: "Gold / SST", color: "#B45309", hex: "#78350F", note: "Clot activator OK after non-additive serum tubes." },
  { step: 5, name: "Green", color: "#15803D", hex: "#14532D", note: "Heparin tubes." },
  { step: 6, name: "Lavender", color: "#7E22CE", hex: "#581C87", note: "EDTA — carry-over can alter chemistries, draw later." },
  { step: 7, name: "Gray", color: "#475569", hex: "#1E293B", note: "Fluoride / oxalate last — most likely to contaminate others." },
];

// Specimen Collection Guide — searchable test catalog.
// Add more entries as your menu evolves. Each entry maps to a real common test.
export const SPECIMEN_TESTS = [
  { test: "CBC (Complete Blood Count)", tube: "Lavender (EDTA)", volume: "3 mL", fasting: "No", processing: "Invert 8–10×. Transport whole blood at RT.", storage: "Room temperature. Process within 24 h.", notes: "Reject if clotted or hemolyzed." },
  { test: "BMP (Basic Metabolic Panel)", tube: "Gold / SST", volume: "5 mL", fasting: "No", processing: "Clot 30 min, centrifuge, separate serum.", storage: "Refrigerated serum up to 7 days.", notes: "Avoid hemolysis (affects K+)." },
  { test: "CMP (Comprehensive Metabolic Panel)", tube: "Gold / SST", volume: "5 mL", fasting: "Yes, 8–12 h preferred", processing: "Clot 30 min, centrifuge, separate serum.", storage: "Refrigerated serum up to 7 days.", notes: "Include liver enzymes — list fasting status." },
  { test: "Hemoglobin A1C", tube: "Lavender (EDTA)", volume: "3 mL", fasting: "No", processing: "Invert. Whole blood — DO NOT centrifuge.", storage: "Refrigerated up to 7 days.", notes: "Stable; reflects 3-month glucose avg." },
  { test: "Lipid Panel", tube: "Gold / SST", volume: "5 mL", fasting: "Yes, 9–12 h", processing: "Serum separation as above.", storage: "Refrigerated.", notes: "Confirm fasting; note last meal time if not fasted." },
  { test: "PT / INR", tube: "Light Blue", volume: "Full draw to fill line", fasting: "No", processing: "Invert 3–4×. Centrifuge 1500×g 15 min.", storage: "Plasma, run within 4 h or freeze.", notes: "Short-draw tubes MUST be rejected." },
  { test: "PTT", tube: "Light Blue", volume: "Full draw", fasting: "No", processing: "Same as PT/INR.", storage: "Plasma, run within 4 h.", notes: "Short-draw = reject." },
  { test: "Glucose (Fasting)", tube: "Gray", volume: "2 mL", fasting: "Yes, 8 h", processing: "Invert 8–10×; process immediately.", storage: "Refrigerate.", notes: "Fluoride prevents glycolysis." },
  { test: "Lactate", tube: "Gray", volume: "2 mL", fasting: "No", processing: "Process within 15 min.", storage: "Refrigerate on ice during transport.", notes: "Time-sensitive — STAT processing." },
  { test: "TSH", tube: "Gold / SST", volume: "3 mL", fasting: "No", processing: "Standard serum prep.", storage: "Refrigerated up to 7 days.", notes: "Note medications (biotin can interfere)." },
  { test: "Vitamin D (25-OH)", tube: "Gold / SST", volume: "3 mL", fasting: "No", processing: "Standard serum prep.", storage: "Refrigerated; protect from light.", notes: "Light-sensitive — wrap in foil." },
  { test: "Iron Panel", tube: "Gold / SST", volume: "5 mL", fasting: "Yes, 12 h, AM draw", processing: "Standard serum prep.", storage: "Refrigerated.", notes: "Avoid hemolysis (skews iron levels)." },
  { test: "Blood Culture (Aerobic + Anaerobic)", tube: "Blood Culture Bottles", volume: "8–10 mL per bottle", fasting: "No", processing: "Strict aseptic technique. Inoculate immediately.", storage: "Transport at room temperature; deliver STAT.", notes: "Always FIRST in the order of draw." },
];

export const HANDLING_STEPS = [
  { n: 1, title: "Verify Patient Identity", body: "Two patient identifiers (name + DOB minimum). Confirm against the order." },
  { n: 2, title: "Verify Test Orders", body: "Cross-check requisition, test codes, fasting/medication requirements, and tube types." },
  { n: 3, title: "Collect Specimen", body: "Use correct tube type, draw to the fill line, follow CLSI order of draw, invert per additive." },
  { n: 4, title: "Label Specimen", body: "Label IN FRONT OF the patient. Include name, DOB, date/time, collector initials." },
  { n: 5, title: "Package Specimen", body: "Biohazard bag with absorbent material. Requisition in the outer pocket only — never with the specimen." },
  { n: 6, title: "Store Correctly", body: "Match temperature requirements: RT, refrigerated, or frozen. Track stability windows." },
  { n: 7, title: "Transport to Laboratory", body: "Use insulated container with appropriate temperature control. Maintain chain of custody." },
  { n: 8, title: "Confirm Delivery", body: "Obtain signature or system confirmation. Resolve any exceptions before leaving the lab." },
];

export const LABELING_STANDARDS = [
  "Label at the patient's side — never pre-labeled.",
  "Required: First + last name, DOB, date, time, collector initials.",
  "Print legibly OR use barcode label from the LIMS.",
  "Wrap the label around the tube — leave a window to view the level.",
  "Any unlabeled or mismatched specimen MUST be rejected and recollected.",
];

export const PACKAGING_STANDARDS = [
  "Use a leak-proof primary container (the tube itself).",
  "Place inside a sealed biohazard bag with absorbent material.",
  "Requisition in the OUTER pocket — never touching the specimen.",
  "Cushion fragile specimens in foam or bubble wrap during transport.",
  "External label: biohazard symbol + 'For Laboratory Use Only'.",
];

export const TEMPERATURE_REQUIREMENTS = [
  { kind: "Room Temperature (15–25 °C)", examples: ["CBC", "PT/INR (run within 4 h)", "Blood Cultures"] },
  { kind: "Refrigerated (2–8 °C)", examples: ["Most chemistries after centrifugation", "Serum aliquots", "Hormones"] },
  { kind: "Frozen (≤ –20 °C)", examples: ["Plasma for coag if not run within 4 h", "Cytokines", "Long-term storage"] },
  { kind: "On Ice (slurry)", examples: ["Lactate", "Ammonia", "Blood gases"] },
];

export const TRANSPORT_SOP = {
  collection_documentation: ["Date", "Time", "Collector", "Location"],
  transport_documentation: ["Pickup Time", "Delivery Time", "Laboratory Destination"],
  chain_of_custody: ["Collector", "Transporter", "Receiver"],
  delivery_confirmation: ["Delivered", "Accepted", "Exception Noted"],
};

export const SHARPS_PROCEDURES = [
  { title: "Needle Safety", body: "Never recap. Activate safety device immediately after withdrawal." },
  { title: "Sharps Container Usage", body: "Drop sharps in upright, FDA-cleared puncture-resistant container at the point of use." },
  { title: "Safe Transport Procedures", body: "Close container fully when 3/4 full. Transport upright in a secondary container." },
  { title: "Disposal Requirements", body: "Schedule pickup with a licensed medical waste vendor. Document chain of custody." },
  { title: "OSHA Safety Practices", body: "Comply with the Bloodborne Pathogen Standard (29 CFR 1910.1030). Annual training required." },
];

export const SHARPS_CHECKLIST = [
  "Sharps Container Present",
  "Container Not Overfilled (≤ 3/4 full)",
  "PPE Available (gloves, mask, eye protection)",
  "Disposal Procedures Followed",
];

export const EXPOSURE_NEEDLESTICK = [
  { n: 1, title: "Wash Area Immediately", body: "Soap + running water for at least 15 seconds. Do NOT squeeze the wound." },
  { n: 2, title: "Notify Supervisor", body: "Inform Beckett or Jazmin immediately." },
  { n: 3, title: "Document Incident", body: "Record patient ID (if known), time, place, mechanism." },
  { n: 4, title: "Seek Medical Evaluation", body: "Go to ER or occupational health within 1–2 hours for PEP eligibility." },
  { n: 5, title: "Complete Incident Report", body: "Fill out the OSHA Sharps Injury Log (29 CFR 1904)." },
  { n: 6, title: "Record Follow-Up Actions", body: "Baseline labs, source-patient testing (with consent), and 6-month follow-up testing." },
];

export const EXPOSURE_BLOOD = [
  { n: 1, title: "Flush Exposed Area", body: "Eyes: emergency eye-wash 15+ min. Skin: soap + water. Mouth: rinse + spit, do not swallow." },
  { n: 2, title: "Report Exposure", body: "Notify supervisor immediately." },
  { n: 3, title: "Document Event", body: "Time, location, source, type of exposure." },
  { n: 4, title: "Obtain Medical Evaluation", body: "Occupational health within 1–2 hours. Follow PEP protocol." },
];

export const SUPPLY_CATEGORY_META = [
  { value: "collection", label: "Collection Supplies", color: "#7E22CE", bg: "rgba(126,34,206,0.10)" },
  { value: "specimen", label: "Specimen Supplies", color: "#B45309", bg: "rgba(180,83,9,0.10)" },
  { value: "patient_care", label: "Patient Care Supplies", color: "#0F6D5D", bg: "rgba(15,109,93,0.10)" },
  { value: "safety", label: "Safety Supplies", color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
  { value: "transport", label: "Transport Supplies", color: "#0B2545", bg: "rgba(11,37,69,0.10)" },
];

// =====================================================================
// Training & Competency
// =====================================================================
export const TRAINING_MODULES = [
  {
    id: "phlebotomy_fundamentals",
    title: "Phlebotomy Fundamentals",
    duration: "4 hours",
    body: "Patient identification, venipuncture site selection, anatomy, and CLSI order of draw. Practical: 25 successful supervised draws.",
    competencies: ["Two-identifier verification", "Tourniquet placement", "Site palpation", "Order of draw", "Specimen labeling at the bedside"],
  },
  {
    id: "specimen_handling",
    title: "Specimen Handling & Transport",
    duration: "2 hours",
    body: "Tube types, additive carryover, inversion, temperature requirements, biohazard packaging, and chain of custody.",
    competencies: ["Tube selection", "Correct inversions", "Temperature compliance", "Biohazard bag packaging", "Chain-of-custody log entries"],
  },
  {
    id: "infection_control",
    title: "Infection Control & PPE",
    duration: "1 hour",
    body: "Standard precautions, donning/doffing PPE, hand hygiene, and bloodborne pathogen exposure prevention.",
    competencies: ["Proper hand hygiene", "Donning PPE", "Doffing PPE", "Disinfection of work surfaces"],
  },
  {
    id: "sharps_osha",
    title: "Sharps Safety & OSHA Compliance",
    duration: "1 hour",
    body: "Needlestick prevention, safe disposal, sharps container handling, and OSHA Bloodborne Pathogen Standard (29 CFR 1910.1030).",
    competencies: ["Activate safety device", "Never recap", "Sharps container fill limits", "Incident reporting"],
  },
  {
    id: "hipaa_privacy",
    title: "HIPAA & Patient Privacy",
    duration: "1 hour",
    body: "Protected health information, minimum-necessary standard, secure communication, and breach reporting protocol.",
    competencies: ["Identify PHI", "Secure document handling", "Verbal privacy in the field", "Report a suspected breach"],
  },
  {
    id: "cpr_bls",
    title: "CPR / BLS Certification",
    duration: "4 hours (initial)",
    body: "American Heart Association BLS for Healthcare Providers — required current certification on file before field deployment.",
    competencies: ["Adult CPR", "AED operation", "Choking response", "2-rescuer technique"],
  },
  {
    id: "customer_service",
    title: "Patient Experience & Customer Service",
    duration: "1 hour",
    body: "Professional communication, anxious-patient techniques, cultural sensitivity, and on-site conduct standards.",
    competencies: ["Introductions & consent", "De-escalation", "Service recovery", "Time management"],
  },
];

// Annual competency checklist — every active phlebotomist/courier signs off.
export const ANNUAL_COMPETENCY = [
  { area: "Patient Identification", standard: "Two patient identifiers verified before every collection." },
  { area: "Venipuncture Technique", standard: "≤ 2 sticks per encounter; documented redirect protocol if unsuccessful." },
  { area: "Order of Draw", standard: "CLSI order followed without deviation; quizzed annually." },
  { area: "Specimen Labeling", standard: "Labeled at the patient's side with name, DOB, date, time, collector initials." },
  { area: "Tube Inversions", standard: "Correct inversions per additive (3–10× depending on tube type)." },
  { area: "Temperature Compliance", standard: "Coolers and ice packs used per test stability requirements." },
  { area: "Sharps Disposal", standard: "Containers replaced at ≤ 3/4 fill; no recapping observed." },
  { area: "PPE", standard: "Gloves and eye protection used for every venipuncture; mask per facility policy." },
  { area: "HIPAA Conduct", standard: "PHI discussed only with verified parties; documents secured at all times." },
  { area: "Chain of Custody", standard: "Pickup/delivery times logged; receiving signature obtained." },
];

// Required documentation that must be current on every staff file.
export const STAFF_CREDENTIAL_REQUIREMENTS = [
  { name: "Phlebotomy Certification", renewal: "Per state — typically every 2 years", required: true },
  { name: "CPR / BLS (AHA)", renewal: "Every 2 years", required: true },
  { name: "HIPAA Training Acknowledgement", renewal: "Annually", required: true },
  { name: "Bloodborne Pathogen Training", renewal: "Annually (OSHA 29 CFR 1910.1030)", required: true },
  { name: "Annual Competency Sign-Off", renewal: "Annually", required: true },
  { name: "TB Test / PPD", renewal: "Annually", required: true },
  { name: "Background Check", renewal: "Every 3 years", required: true },
  { name: "Auto Insurance (couriers/transport)", renewal: "Annually", required: false },
];
